import { NOTIFICATION_TYPES } from '@optimizely/optimizely-sdk';
import getOptimizelyInstance from './getOptimizelyInstance';
import { activeExperiments, clearAllListeners, clearListener, addNotificationListener } from './decisionListenerHelpers';

/**
 * Notification handler for ACTIVATE event
 * @param {String} userId - The user ID for whom the activation occurred
 * @param {*} activationEvent - The activation event data
 */
const onActivate = (_experimentIdMap, userId, _listenerConfig, activationEvent) => {
  const {
    experiment = {},
    variation = {}
  } = activationEvent;
  if (experiment.key && variation.key) {
    activeExperiments[userId] = activeExperiments[userId] || [];
    if (!activeExperiments[userId].includes(experiment.key + ':' + variation.key)) {
      activeExperiments[userId].push(experiment.key + ':' + variation.key);
    }
  }
};

/**
 * Notification handler for DECISION event
 * @param {Object} experimentIdMap - Map of experiment IDs to configs
 * @param {String} userId - The user ID for whom the decision was made
 * @param {Object} listenerConfig - Configuration with callback functions
 * @param {Object} decisionEvent - The decision event data
 */
const onDecision = (experimentIdMap, userId, listenerConfig, decisionEvent) => {
  const {
    decisionInfo = {},
    type
  } = decisionEvent;
  if (type === 'flag') {
    const {
      experimentId,
      variationKey
    } = decisionInfo;
    const experiment = experimentIdMap?.[experimentId];
    if (experiment && variationKey) {
      const {
        key: experimentKey
      } = experiment;
      if (!activeExperiments[userId]?.includes(experimentKey + ':' + variationKey)) {
        activeExperiments[userId] = activeExperiments[userId] || [];
        activeExperiments[userId].push(experimentKey + ':' + variationKey);
      }
      listenerConfig?.onDecision?.(activeExperiments[userId], decisionInfo);
    }
  }
};

/**
 * Clears active experiments for a specific user
 * @param {String} userId - The user ID to clear active experiments for (optional)
 */
export const clearActiveExperiments = userId => {
  const optimizely = getOptimizelyInstance();
  if (userId) {
    delete activeExperiments[userId];
    clearListener(optimizely, NOTIFICATION_TYPES.DECISION, userId);
    return;
  }
  Object.keys(activeExperiments).forEach(key => {
    delete activeExperiments[key];
  });
  clearAllListeners(optimizely);
};

/**
 * Get active experiments
 * @param {string} userId - The user ID to get active experiments for (optional)
 * @returns {Array} Get active experiments for a specific user or all users if no userId is provided
 */
export const getActiveExperiments = userId => {
  if (!userId) {
    const result = new Set(Object.values(activeExperiments).flat());
    return Array.from(result);
  }
  return activeExperiments[userId] || [];
};

/**
 * Optimizely Test Decision Listener
 * @param {Object} optimizely - The optimizely instance
 * @param {Object} listenerConfig - The listener configuration
 * @param {String} userId - The user ID
 */
const OptimizelyTestDecisionListener = (optimizely, listenerConfig, userId) => {
  if (optimizely) {
    const {
      experimentsMap
    } = optimizely.getOptimizelyConfig();
    const values = Object.values(experimentsMap || {});
    const experimentIdMap = Object.fromEntries(values.map(exp => [exp.id, exp]));
    const registerNotificationId = addNotificationListener(optimizely, NOTIFICATION_TYPES.DECISION, onDecision, {
      experimentIdMap,
      userId,
      listenerConfig
    });
    addNotificationListener(optimizely, NOTIFICATION_TYPES.ACTIVATE, onActivate, {
      userId
    });

    // For logging when the listener is registered
    listenerConfig?.onRegistered?.(registerNotificationId);
  }
};
export default OptimizelyTestDecisionListener;